import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { updateConnectorsWithAccounts } from 'src/hooks/utils';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebookLogo.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify2.svg';
import { ReactComponent as MagentoIcon } from 'src/icons/magento.svg';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import {
  createGetAccountsQuery,
  GET_CONNECTORS,
  PLACEHOLDER_QUERY,
} from 'src/utils/gql/queries';
import {
  AnalyticsAccount,
  Connector,
  ConnectorType,
  FacebookAdAccount,
  GoogleAdsAccount,
  Query,
  QueryGetConnectorsArgs,
  SearchConsoleSite,
} from 'src/utils/gql/types';

interface ConnectorAccountDetails {
  icon: ReactNode;
  title: string;
  description: string;
  button: string;
}

type ConnectorAccount = {
  [K in ConnectorType]: ConnectorAccountDetails;
};

export interface GetAnalyticsAccounts {
  [key: string]: AnalyticsAccount[];
}

export interface GetGoogleAdsAccounts {
  [key: string]: GoogleAdsAccount[];
}

interface GetFacebookAdAccounts {
  [key: string]: FacebookAdAccount[];
}

interface Account {
  id: string;
  name: string;
  selected: boolean;
  properties: { id: string; name: string; selected: boolean }[];
}

export interface FormattedConnector extends Connector {
  accounts: Account[];
}

const conectorAccountData: ConnectorAccount = {
  [ConnectorType.FACEBOOK]: {
    icon: <FacebookAdsIcon />,
    title: 'Connect your Facebook accounts',
    description: 'Advertising platform',
    button: 'Add Facebook account',
  },
  [ConnectorType.GOOGLE_ADS]: {
    icon: <GoogleAdsIcon width={40} height={40} />,
    title: 'Connect your Google Ads accounts',
    description: 'Advertising platform',
    button: 'Add Google aDS account',
  },
  [ConnectorType.GOOGlE_ANALYTICS4]: {
    icon: <GoogleAnalyticsIcon width={40} height={40} />,
    title: 'Connect your Google Analytics 4 accounts',
    description: 'Anatylics platform',
    button: 'Add Google analytics 4 account',
  },
  [ConnectorType.GOOGLE_SEARCH_CONSOLE]: {
    icon: <GoogleSearchConsoleIcon width={40} height={40} />,
    title: 'Connect your Google Search Console sites',
    description: 'Organic search performance',
    button: 'Add Search Console site',
  },
  [ConnectorType.SHOPIFY]: {
    icon: <ShopifyIcon />,
    title: 'Connect your Shopify accounts',
    description: 'Multi-channel e-commerce platform',
    button: 'Add shopify account',
  },
  [ConnectorType.KIPIFY]: {
    icon: (
      <div
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: '#222822',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#1EED9F',
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        K
      </div>
    ),
    title: 'Kipify Native Features',
    description: 'Cross-connector data normalization',
    button: 'Use Kipify features',
  },
  [ConnectorType.DATABASE]: {
    icon: (
      <div
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: '#1976d2',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
        }}
      >
        DB
      </div>
    ),
    title: 'Database Connector',
    description: 'Connect to external databases',
    button: 'Add Database connection',
  },
  [ConnectorType.MAGENTO]: {
    icon: <MagentoIcon width={40} height={40} />,
    title: 'Connect your Magento store',
    description: 'Adobe Commerce / Magento 2',
    button: 'Add Magento connection',
  },
};

export const useConnectorAccount = ({ type }: { type: ConnectorType }) => {
  const snackbar = useSnackbar();
  const [connectors, setConnectors] = useState<FormattedConnector[]>([]);

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const { data: connectorsData, loading: connectorsLoading } = useQuery<
    Pick<Query, 'getConnectors'>,
    QueryGetConnectorsArgs
  >(GET_CONNECTORS, {
    variables: { type },
    onError,
    onCompleted: ({ getConnectors }) => {
      const formattedConnectors = updateConnectorsWithAccounts(
        getConnectors,
        {},
        type,
      );
      setConnectors(formattedConnectors);
    },
  });

  const accountsQuery = useMemo(() => {
    const connectorsIds =
      connectorsData?.getConnectors?.map(({ id }) => id) || [];
    const shouldFetchAccounts =
      type !== ConnectorType.SHOPIFY &&
      type !== ConnectorType.DATABASE &&
      type !== ConnectorType.MAGENTO &&
      connectorsIds.length;

    if (shouldFetchAccounts) {
      return createGetAccountsQuery(connectorsIds, type);
    } else {
      return PLACEHOLDER_QUERY;
    }
  }, [connectorsData?.getConnectors, type]);

  const { loading: accountsLoading } = useQuery<
    | GetAnalyticsAccounts
    | GetGoogleAdsAccounts
    | GetFacebookAdAccounts
    | { [key: string]: SearchConsoleSite[] }
  >(accountsQuery, {
    onError,
    onCompleted: (accountsData) => {
      const formattedConnectors = updateConnectorsWithAccounts(
        connectorsData?.getConnectors || [],
        accountsData,
        type,
      );
      setConnectors(formattedConnectors);
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const connectorDetails = useMemo(() => conectorAccountData[type], [type]);

  const handleChangeConnectors = useCallback(
    (conectors: FormattedConnector[]) => setConnectors(conectors),
    [],
  );

  return {
    connectors,
    connectorDetails,
    loading: connectorsLoading || accountsLoading,
    handleChangeConnectors,
  };
};
